import { useState, useEffect } from "react";
import {
    Row,
    Col,
    Card,
    message,
    Table,
    Tag,
    Select,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    Space,
} from "antd";
import {
    list_polars_quota_requestsApi,
    approve_polars_quota_requestApi,
    reject_polars_quota_requestApi,
} from "../api/admin.billing.apis";

const { Option } = Select;
const { TextArea } = Input;

const STATUS_COLORS = {
    pending: "orange",
    approved: "green",
    rejected: "red",
};

function PolarsQuotaRequestsManagement() {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [statusFilter, setStatusFilter] = useState("pending");
    const [actionLoading, setActionLoading] = useState(false);
    const [reviewModal, setReviewModal] = useState({ open: false, type: null, record: null });
    const [form] = Form.useForm();

    useEffect(() => {
        fetchRequests(1, statusFilter);
    }, []);

    const fetchRequests = async (page = 1, status = statusFilter) => {
        try {
            setLoading(true);
            const params = { page, page_size: pagination.pageSize };
            if (status) params.status = status;
            const response = await list_polars_quota_requestsApi(params);
            if (response.data.status === "success") {
                const data = response.data.data || {};
                setRequests(
                    (data.requests || []).map((r) => ({ ...r, key: r.id }))
                );
                setPagination((prev) => ({
                    ...prev,
                    current: data.page || page,
                    total: data.total || 0,
                }));
            } else {
                message.error(response.data.message || "Failed to load requests");
            }
        } catch (error) {
            console.error(error);
            message.error("Error loading Smart AI Summary quota requests");
        } finally {
            setLoading(false);
        }
    };

    const openReview = (record, type) => {
        setReviewModal({ open: true, type, record });
        form.setFieldsValue({
            granted_amount: record.requested_amount || 50,
            admin_note: "",
        });
    };

    const handleReview = async (values) => {
        const { record, type } = reviewModal;
        if (!record) return;
        try {
            setActionLoading(true);
            const payload = {
                request_id: record.id,
                admin_note: values.admin_note,
            };
            if (type === "approve") {
                payload.granted_amount = values.granted_amount;
            }
            const api =
                type === "approve"
                    ? approve_polars_quota_requestApi
                    : reject_polars_quota_requestApi;
            const response = await api(payload);
            if (response.data.status === "success") {
                message.success(
                    type === "approve" ? "Request approved" : "Request rejected"
                );
                setReviewModal({ open: false, type: null, record: null });
                form.resetFields();
                fetchRequests(pagination.current, statusFilter);
            } else {
                message.error(response.data.message || "Action failed");
            }
        } catch (error) {
            console.error(error);
            message.error(error.response?.data?.message || "Action failed");
        } finally {
            setActionLoading(false);
        }
    };

    const columns = [
        { title: "User", dataIndex: "user_email", key: "user_email" },
        {
            title: "Requested",
            dataIndex: "requested_amount",
            key: "requested_amount",
            render: (v) => (v != null ? `+${v}` : "—"),
        },
        {
            title: "Message",
            dataIndex: "message",
            key: "message",
            ellipsis: true,
            render: (v) => v || "—",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Tag color={STATUS_COLORS[status] || "default"}>{status}</Tag>
            ),
        },
        {
            title: "Granted",
            dataIndex: "granted_amount",
            key: "granted_amount",
            render: (v) => (v != null ? `+${v}` : "—"),
        },
        {
            title: "Submitted",
            dataIndex: "created_at",
            key: "created_at",
            render: (v) => (v ? new Date(v).toLocaleString() : "—"),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) =>
                record.status === "pending" ? (
                    <Space>
                        <Button
                            type="primary"
                            size="small"
                            loading={actionLoading}
                            onClick={() => openReview(record, "approve")}
                        >
                            Approve
                        </Button>
                        <Button
                            danger
                            size="small"
                            loading={actionLoading}
                            onClick={() => openReview(record, "reject")}
                        >
                            Reject
                        </Button>
                    </Space>
                ) : (
                    <span className="theme-text-secondary">
                        {record.admin_email || "—"}
                    </span>
                ),
        },
    ];

    return (
        <>
            <Row gutter={[24, 24]}>
                <Col span={24}>
                    <Card title="Smart AI Summary Quota Requests">
                        <Space wrap style={{ marginBottom: 16 }}>
                            <Select
                                value={statusFilter}
                                style={{ width: 160 }}
                                onChange={(v) => {
                                    setStatusFilter(v);
                                    fetchRequests(1, v);
                                }}
                            >
                                <Option value="pending">Pending</Option>
                                <Option value="approved">Approved</Option>
                                <Option value="rejected">Rejected</Option>
                                <Option value="">All</Option>
                            </Select>
                            <Button onClick={() => fetchRequests(pagination.current, statusFilter)}>
                                Refresh
                            </Button>
                        </Space>
                        <Table
                            columns={columns}
                            dataSource={requests}
                            loading={loading}
                            pagination={{
                                ...pagination,
                                onChange: (page) => fetchRequests(page, statusFilter),
                            }}
                        />
                    </Card>
                </Col>
            </Row>

            <Modal
                title={
                    reviewModal.type === "approve"
                        ? "Approve Smart AI Summary quota request"
                        : "Reject Smart AI Summary quota request"
                }
                open={reviewModal.open}
                onCancel={() => setReviewModal({ open: false, type: null, record: null })}
                onOk={() => form.submit()}
                confirmLoading={actionLoading}
                destroyOnClose
            >
                {reviewModal.record && (
                    <p style={{ marginBottom: 12 }}>
                        <strong>{reviewModal.record.user_email}</strong>
                        {reviewModal.record.message ? ` — ${reviewModal.record.message}` : ""}
                    </p>
                )}
                <Form form={form} layout="vertical" onFinish={handleReview}>
                    {reviewModal.type === "approve" && (
                        <Form.Item
                            name="granted_amount"
                            label="Grant additional Smart AI summaries"
                            rules={[{ required: true, message: "Amount is required" }]}
                        >
                            <InputNumber min={1} max={10000} style={{ width: "100%" }} />
                        </Form.Item>
                    )}
                    <Form.Item name="admin_note" label="Admin note (optional)">
                        <TextArea rows={3} placeholder="Internal note or reason for the user" />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}

export default PolarsQuotaRequestsManagement;
